import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	Output,
	EventEmitter,
	SimpleChanges,
	ViewEncapsulation,
	forwardRef
} from '@angular/core';
import { IdService, FormControlBase } from '../../shared/index';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
	selector: 'rbc-textarea',
	templateUrl: './textarea.component.html',
	styleUrls: ['./textarea.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => TextareaComponent)
		}
	]
})
export class TextareaComponent extends FormControlBase<string> implements OnInit, OnDestroy {
	// Private Members
	id: string;
	showError: boolean = false;

	// Input Parameters
	@Input() cols: number | false = false;
	@Input() disabled: boolean = false;
	@Input() errorLabel: string = 'Invalid';
	@Input() helpText: string;
	@Input() label: string;
	@Input() maxlength: number;
	@Input() name: string;
	@Input() placeholder: string;
	@Input() readonly: boolean = false;
	@Input() required: boolean = false;
	@Input() resizing: boolean = false;
	@Input() rows: number | false = false;
	@Input() wrap: 'hard' | 'soft';
	@Input() model: string;

	// The event when the model is changed.
	@Output() modelChange = new EventEmitter();

	// Inject the ID service to generate a UUID
	constructor(private idService: IdService) {
		super();
		this.id = idService.generate();
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes && changes.model) {
			this.writeValue(changes.model.currentValue);
		}
	}

	ngOnInit() {
		// The rows value is supposed to be a required field.
		if (this.rows === null) {
			throw new Error('Define the number of rows of the textarea component ' + this.id);
		}
	}

	ngOnDestroy(): void {
		// Remove the ID when the component gets destroyed, so the ID can be reused.
		this.idService.remove(this.id);
		super.ngOnDestroy();
	}

	/**
	 * Calculates the classes, primarily to determine if the class `resizeable`
	 * should be applied to the textarea element.
	 */
	calculateClasses(): {} {
		const classes = {};
		classes['resizable'] = this.resizing;
		return classes;
	}

	/**
	 * When the ngModelChange event is fired, validation event is fired.
	 * Updated model gets emitted.
	 */
	processChange(value: string): void {
		this.showError = this.error();
		this.setModel(value);
		this.modelChange.emit(this.innerValue);
	}

	/**
	 * For validation purposes, currently the only validation for this field is required.
	 */
	error(): boolean {
		if (this.required) {
			return this.innerValue === '';
		}

		return false;
	}
}
